import { useEffect, useMemo, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import {
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import {
  ArrowDown, ArrowUp, ChevronLeft, ChevronRight, ChevronsUpDown, Inbox, SlidersHorizontal,
} from 'lucide-react';
import { cn } from './cn';
import { Dropdown } from './Dropdown';
import { Checkbox } from './Checkbox';

export interface DataTableColumn<T> {
  key?: string;
  title: ReactNode;
  dataIndex?: string | string[];
  render?: (value: any, record: T, index: number) => ReactNode;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  sorter?: (a: T, b: T) => number;
  defaultVisible?: boolean;
  alwaysVisible?: boolean;
  ellipsis?: boolean;
}

export interface DataTablePagination {
  pageSize?: number;
  current?: number;
  total?: number;
  showSizeChanger?: boolean;
  onChange?: (page: number, pageSize: number) => void;
}

export interface DataTableProps<T> {
  /** Persists column-visibility + density to localStorage. */
  storageKey: string;
  columns: DataTableColumn<T>[];
  dataSource: T[];
  rowKey?: string | ((r: T) => string | number);
  loading?: boolean;
  pagination?: false | DataTablePagination;
  toolbarLeft?: ReactNode;
  toolbarRight?: ReactNode;
  onRow?: (record: T, index: number) => { onClick?: (e: MouseEvent) => void } | undefined;
  expandable?: {
    expandedRowRender: (record: T, index: number) => ReactNode;
    rowExpandable?: (record: T) => boolean;
  };
  emptyText?: ReactNode;
  /** Min content width before horizontal scroll kicks in. */
  scrollX?: number;
  className?: string;
}

type Density = 'compact' | 'default' | 'spacious';
const PAD: Record<Density, string> = {
  compact: 'px-3 py-1.5',
  default: 'px-3.5 py-2.5',
  spacious: 'px-4 py-3.5',
};
const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

const getByPath = (obj: any, path?: string | string[]) => {
  if (path == null) return undefined;
  if (Array.isArray(path)) return path.reduce((o, k) => (o == null ? o : o[k]), obj);
  return obj?.[path];
};

function keyOf<T>(c: DataTableColumn<T>): string {
  return c.key ?? (Array.isArray(c.dataIndex) ? c.dataIndex.join('.') : String(c.dataIndex ?? c.title));
}

/**
 * Tailwind data grid (replaces the antd-based DataTable) on TanStack Table.
 * Keeps the same column API, so list pages migrate by swapping the import.
 * Sticky header, client/server pagination, sort, density, column-picker
 * (persisted), expandable rows, loading + empty states.
 */
export function DataTable<T extends object>({
  storageKey, columns, dataSource, rowKey = 'id', loading, pagination = {},
  toolbarLeft, toolbarRight, onRow, expandable, emptyText = 'No data', scrollX, className,
}: DataTableProps<T>) {
  const allKeys = useMemo(() => columns.map(keyOf), [columns]);
  const defaultVisible = useMemo(() => columns.filter((c) => c.defaultVisible !== false).map(keyOf), [columns]);

  // ── persisted column visibility + density ──
  const visKey = `gb:tbl:${storageKey}:visible`;
  const densKey = `gb:tbl:${storageKey}:density`;
  const [visible, setVisible] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(visKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!Array.isArray(parsed)) return defaultVisible;
      const vis = parsed.filter((k: string) => allKeys.includes(k));
      return vis.length ? vis : defaultVisible;
    } catch { return defaultVisible; }
  });
  const [density, setDensity] = useState<Density>(() => {
    // Guard the stored value: the legacy antd DataTable shared this key but used small/middle/large.
    // An unknown value would make PAD[density] undefined and strip ALL cell padding (cramped rows),
    // so migrate the old names and fall back to 'default' for anything unrecognised.
    const stored = localStorage.getItem(densKey);
    if (stored === 'compact' || stored === 'default' || stored === 'spacious') return stored;
    const legacy: Record<string, Density> = { small: 'compact', middle: 'default', large: 'spacious' };
    return (stored && legacy[stored]) || 'default';
  });
  useEffect(() => { try { localStorage.setItem(visKey, JSON.stringify(visible)); } catch { /* ignore */ } }, [visKey, visible]);
  useEffect(() => { try { localStorage.setItem(densKey, density); } catch { /* ignore */ } }, [densKey, density]);

  const visibleSet = useMemo(() => new Set(visible), [visible]);
  const shownColumns = columns.filter((c) => c.alwaysVisible || visibleSet.has(keyOf(c)));

  // ── server vs client pagination ──
  const serverPaginated = pagination !== false && (pagination.current != null || pagination.total != null);

  const [sorting, setSorting] = useState<SortingState>([]);
  const tanColumns = useMemo<ColumnDef<T>[]>(
    () => shownColumns.map((c) => ({
      id: keyOf(c),
      accessorFn: (row: T) => getByPath(row, c.dataIndex),
      enableSorting: !!(c.sortable || c.sorter),
      sortingFn: c.sorter ? (a, b) => c.sorter!(a.original, b.original) : 'auto',
    })),
    [shownColumns],
  );

  const table = useReactTable({
    data: dataSource,
    columns: tanColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: pagination !== false && !serverPaginated ? getPaginationRowModel() : undefined,
    manualPagination: serverPaginated,
    initialState: pagination !== false && !serverPaginated ? { pagination: { pageSize: pagination.pageSize ?? 10 } } : undefined,
  });

  const rows = table.getRowModel().rows;

  const [expanded, setExpanded] = useState<Set<string | number>>(new Set());
  const keyForRow = (r: T, i: number) => (typeof rowKey === 'function' ? rowKey(r) : (r as any)[rowKey] ?? i);
  const toggleExpand = (k: string | number) =>
    setExpanded((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  // ── pagination footer numbers ──
  const clientPage = table.getState().pagination.pageIndex;
  const clientPageSize = table.getState().pagination.pageSize;
  const total = serverPaginated ? (pagination as DataTablePagination).total ?? dataSource.length : dataSource.length;
  const curPage = serverPaginated ? ((pagination as DataTablePagination).current ?? 1) : clientPage + 1;
  const curSize = serverPaginated ? (pagination as DataTablePagination).pageSize ?? 10 : clientPageSize;
  const pageCount = Math.max(1, Math.ceil(total / curSize));
  const from = total === 0 ? 0 : (curPage - 1) * curSize + 1;
  const to = Math.min(curPage * curSize, total);
  const goPage = (p: number) => {
    if (serverPaginated) (pagination as DataTablePagination).onChange?.(p, curSize);
    else table.setPageIndex(p - 1);
  };

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Toolbar */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex flex-1 flex-wrap items-center gap-2">{toolbarLeft}</div>
        <div className="flex flex-wrap items-center gap-2">
          {toolbarRight}
          <div className="inline-flex overflow-hidden rounded-control border border-line">
            {(['compact', 'default', 'spacious'] as Density[]).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDensity(d)}
                className={cn('px-2.5 py-1 text-xs capitalize transition-colors',
                  density === d ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' : 'text-muted hover:bg-surface-2')}
              >
                {d}
              </button>
            ))}
          </div>
          <Dropdown
            trigger="click"
            align="end"
            panelClassName="min-w-[220px] p-2"
            panel={
              <div>
                <div className="px-1 pb-2 text-xs font-semibold text-subtle">Show columns</div>
                <div className="flex flex-col gap-1.5">
                  {columns.filter((c) => !c.alwaysVisible).map((c) => {
                    const k = keyOf(c);
                    return (
                      <Checkbox
                        key={k}
                        checked={visibleSet.has(k)}
                        onCheckedChange={(on) => setVisible((v) => (on ? [...v, k] : v.filter((x) => x !== k)))}
                        label={typeof c.title === 'string' ? c.title : k}
                      />
                    );
                  })}
                </div>
                <div className="mt-2 flex gap-3 border-t border-line pt-2 text-xs">
                  <button type="button" className="text-brand-700 hover:underline" onClick={() => setVisible(allKeys)}>Show all</button>
                  <button type="button" className="text-muted hover:underline" onClick={() => setVisible(defaultVisible)}>Reset</button>
                </div>
              </div>
            }
          >
            <button type="button" aria-label="Column settings" className="grid size-8 place-items-center rounded-control border border-line text-muted transition-colors hover:border-brand-300 hover:text-brand-700">
              <SlidersHorizontal size={15} />
            </button>
          </Dropdown>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-card border border-line">
        <table className="w-full border-collapse text-sm" style={scrollX ? { minWidth: scrollX } : undefined}>
          <thead className="sticky top-0 z-10 bg-surface-2">
            <tr className="border-b border-line">
              {expandable && <th className="w-9" />}
              {shownColumns.map((c) => {
                const col = table.getColumn(keyOf(c));
                const sortDir = col?.getIsSorted();
                return (
                  <th
                    key={keyOf(c)}
                    style={{ width: c.width }}
                    className={cn('whitespace-nowrap px-3.5 py-2.5 text-[12px] font-semibold uppercase tracking-wide text-muted', ALIGN[c.align ?? 'left'])}
                  >
                    {col?.getCanSort() ? (
                      <button type="button" onClick={col.getToggleSortingHandler()} className={cn('inline-flex items-center gap-1 hover:text-content', c.align === 'right' && 'flex-row-reverse')}>
                        {c.title}
                        {sortDir === 'asc' ? <ArrowUp size={13} className="text-brand-600" /> : sortDir === 'desc' ? <ArrowDown size={13} className="text-brand-600" /> : <ChevronsUpDown size={13} className="opacity-40" />}
                      </button>
                    ) : (
                      c.title
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={`sk${i}`} className="border-b border-line">
                  {expandable && <td className="px-3.5 py-3" />}
                  {shownColumns.map((c) => (
                    <td key={keyOf(c)} className={cn(PAD[density])}>
                      <div className="h-3.5 w-3/4 animate-pulse rounded bg-surface-3" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={shownColumns.length + (expandable ? 1 : 0)}>
                  <div className="flex flex-col items-center justify-center gap-2 py-14 text-subtle">
                    <Inbox size={28} className="opacity-50" />
                    <span className="text-sm">{emptyText}</span>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const rec = row.original;
                const rk = keyForRow(rec, row.index);
                const rowProps = onRow?.(rec, row.index);
                const isExpanded = expanded.has(rk);
                const canExpand = expandable && (expandable.rowExpandable?.(rec) ?? true);
                return (
                  <FragmentRow key={rk}>
                    <tr
                      onClick={rowProps?.onClick}
                      className={cn('border-b border-line transition-colors hover:bg-surface-2', rowProps?.onClick && 'cursor-pointer')}
                    >
                      {expandable && (
                        <td className="w-9 pl-2">
                          {canExpand && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleExpand(rk); }}
                              className="grid size-6 place-items-center rounded-md text-muted transition-colors hover:bg-surface-3 hover:text-content"
                              aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            >
                              <ChevronRight size={15} className={cn('transition-transform', isExpanded && 'rotate-90')} />
                            </button>
                          )}
                        </td>
                      )}
                      {shownColumns.map((c) => {
                        const val = getByPath(rec, c.dataIndex);
                        return (
                          <td
                            key={keyOf(c)}
                            className={cn('text-content', PAD[density], ALIGN[c.align ?? 'left'], c.ellipsis && 'max-w-[1px] truncate')}
                            title={c.ellipsis && typeof val === 'string' ? val : undefined}
                          >
                            {c.render ? c.render(val, rec, row.index) : (val as ReactNode)}
                          </td>
                        );
                      })}
                    </tr>
                    {expandable && isExpanded && (
                      <tr className="border-b border-line bg-surface-2/40">
                        <td colSpan={shownColumns.length + 1} className="px-4 py-3">
                          {expandable.expandedRowRender(rec, row.index)}
                        </td>
                      </tr>
                    )}
                  </FragmentRow>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / pagination */}
      {pagination !== false && total > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-muted">
          <span>
            {from}–{to} of {total}
          </span>
          <div className="flex items-center gap-1">
            <button type="button" disabled={curPage <= 1} onClick={() => goPage(curPage - 1)} className="grid size-8 place-items-center rounded-control border border-line text-muted transition-colors hover:border-brand-300 hover:text-brand-700 disabled:opacity-40 disabled:hover:border-line disabled:hover:text-muted">
              <ChevronLeft size={16} />
            </button>
            <span className="px-2 tabular-nums">Page {curPage} / {pageCount}</span>
            <button type="button" disabled={curPage >= pageCount} onClick={() => goPage(curPage + 1)} className="grid size-8 place-items-center rounded-control border border-line text-muted transition-colors hover:border-brand-300 hover:text-brand-700 disabled:opacity-40 disabled:hover:border-line disabled:hover:text-muted">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// React fragment with a key (rows + expansion row need to share a key).
function FragmentRow({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
